"use client";

import * as React from "react";
import { Minus, Plus } from "lucide-react";

import { cn } from "../../lib/utils";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
	InputGroupText,
} from "./input-group";

const NUMERIC_CHARS_REGEX = /[^0-9.-]/g;

type IncrementInputOwnProps = {
	/** The amount to increment or decrement the value by. @default 1 */
	step?: number;
	/** The minimum value. @default 0 */
	min?: number;
	/** The maximum value. */
	max?: number;
	/** Number of decimal places to format to on blur. @default 0 */
	precision?: number;
	/** Label that prefixes the input value (e.g. "$"). */
	prefixLabel?: string;
	/** Horizontal alignment of the input text. @default "start" */
	align?: "start" | "center";
	/** Height/text size of the field. `lg` matches the legacy jux-next input. @default "default" */
	size?: "default" | "lg";
	/** When true, applies a danger ring and sets `aria-invalid`. */
	isInvalid?: boolean;
	disabled?: boolean;
	value: string;
	onChange: (value: string) => void;
	onBlur?: (value: number) => void;
	onIncrement?: (value: string) => void;
	onDecrement?: (value: string) => void;
};

export type IncrementInputProps = Omit<
	React.ComponentPropsWithoutRef<"input">,
	keyof IncrementInputOwnProps | "size"
> &
	IncrementInputOwnProps;

const IncrementInput = React.forwardRef<HTMLInputElement, IncrementInputProps>(
	(
		{
			className,
			isInvalid,
			disabled,
			value,
			onChange,
			onBlur,
			onIncrement,
			onDecrement,
			step = 1,
			min = 0,
			max,
			precision = 0,
			prefixLabel,
			align = "start",
			size = "default",
			...props
		},
		ref,
	) => {
		React.useEffect(() => {
			const parsed = parseFloat(value);
			if (!Number.isNaN(parsed)) {
				const formatted = parsed.toFixed(precision);
				if (formatted !== value) {
					onChange(formatted);
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps -- only reformat on mount and when precision changes, not while the user types
		}, [precision]);

		const parsed = parseFloat(value);
		const numericValue = Number.isNaN(parsed) ? min : parsed;

		const clamp = (val: number) => {
			const lower = Math.max(val, min);
			return typeof max !== "undefined" ? Math.min(lower, max) : lower;
		};

		const canIncrement = typeof max !== "undefined" ? numericValue < max : true;
		const canDecrement = numericValue > min;

		const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
			const sanitized = e.target.value.replace(NUMERIC_CHARS_REGEX, "");
			const leading = sanitized.startsWith("-") ? "-" : "";
			const withoutMinus = sanitized.replace(/-/g, "");
			const parts = withoutMinus.split(".");
			const cleaned =
				leading +
				(parts.length > 2
					? `${parts[0]}.${parts.slice(1).join("")}`
					: withoutMinus);
			onChange(cleaned);
		};

		const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
			const blurValue = parseFloat(e.target.value);
			const clamped =
				e.target.value === "" || Number.isNaN(blurValue)
					? clamp(0)
					: clamp(blurValue);
			const formatted = clamped.toFixed(precision);
			onChange(formatted);
			onBlur?.(parseFloat(formatted));
		};

		const handleIncrement = () => {
			if (!canIncrement) {
				return;
			}
			const formatted = clamp(numericValue + step).toFixed(precision);
			onChange(formatted);
			onIncrement?.(formatted);
		};

		const handleDecrement = () => {
			if (!canDecrement) {
				return;
			}
			const formatted = clamp(numericValue - step).toFixed(precision);
			onChange(formatted);
			onDecrement?.(formatted);
		};

		return (
			<InputGroup
				size={size}
				className={cn("bg-background", className)}
				data-disabled={disabled || undefined}
				// `props` go to the input, and the addons are `role="group"` too, so the
				// field's own box needs a handle of its own.
				data-testid="increment-input"
			>
				<InputGroupAddon align="inline-start">
					<InputGroupButton
						size={size === "lg" ? "icon-sm" : "icon-xs"}
						disabled={disabled || !canDecrement}
						onClick={handleDecrement}
						aria-label="Decrement"
					>
						<Minus className="size-4" />
					</InputGroupButton>
				</InputGroupAddon>
				{prefixLabel && (
					<InputGroupAddon align="inline-start">
						<InputGroupText>{prefixLabel}</InputGroupText>
					</InputGroupAddon>
				)}
				<InputGroupInput
					ref={ref}
					type="text"
					inputMode="decimal"
					size={size}
					disabled={disabled}
					aria-invalid={isInvalid || undefined}
					value={value}
					onChange={handleChange}
					onBlur={handleBlur}
					className={cn(align === "center" && "text-center")}
					{...props}
				/>
				<InputGroupAddon align="inline-end">
					<InputGroupButton
						size={size === "lg" ? "icon-sm" : "icon-xs"}
						disabled={disabled || !canIncrement}
						onClick={handleIncrement}
						aria-label="Increment"
					>
						<Plus className="size-4" />
					</InputGroupButton>
				</InputGroupAddon>
			</InputGroup>
		);
	},
);
IncrementInput.displayName = "IncrementInput";

export { IncrementInput };
